import React, { useEffect, useState } from 'react';

import {
    DefaultContentAcquisitionEntity,
    GetDefaultContentAcquisitionEntityInput,
    GetDefaultContentAcquisitionEntityOutput,
    Service
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';

import { ColumnState, ICellRendererParams } from 'ag-grid-community';
import { Switch, useRouteMatch } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { BaseAgGrid } from 'src/components/BaseAgGrid';
import { FilterInput } from 'src/components/FilterInput';
import { VIEWER_ROLE_SET } from 'src/constants/role';
import { AppContext } from 'src/contexts/appContext';
import { ICurrencyAndContentAcquisitionEntityRowData } from 'src/types/ICurrencyAndContentAcquisitionEntity';
import { getTerritoryFlagRenderer } from 'src/util/grid/cellRenderer';
import { buildDefaultCaeMap } from 'src/util/metadataUtil';

import { ProtectedRoute } from '../ProtectedRoute';
import './CurrencyAndContentAcquisitionEntity.scss';

export const CurrencyAndContentAcquisitionEntity = () => {
    const { path } = useRouteMatch();
    const { setTopAlertMsg, metadata } = React.useContext(AppContext);

    const [currencyContentAcquisitionEntityRowData, setCurrencyContentAcquisitionEntityRowData] = useState<
        ICurrencyAndContentAcquisitionEntityRowData[]
    >([]);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [filterText, setFilterText] = useState('');

    const territoryList = Object.values(metadata.territoryMapping);

    useEffect(() => {
        // Fetch default CAEs
        contractConfigurationClient
            .getDefaultContentAcquisitionEntity(
                GetDefaultContentAcquisitionEntityInput.clone({
                    includeUnlaunchedTerritories: true
                })
            )
            .subscribe(
                (response: GetDefaultContentAcquisitionEntityOutput) => {
                    const defaultCaeMap = buildDefaultCaeMap(response.defaultContentAcquisitionEntities);
                    const caeList = Object.values(defaultCaeMap);

                    // Use service list from metadata and filter out ALL service
                    const serviceList = Object.values(metadata.serviceMapping).filter(
                        (service) => service.serviceId !== 0
                    );

                    setCurrencyContentAcquisitionEntityRowData(mapResponseToRowData(caeList, serviceList));
                },
                (error: UnknownCoralError) => setTopAlertMsg(error.receivedException.message),
                () => setIsDataLoaded(true)
            );
    }, []);

    const mapResponseToRowData = (caeList: DefaultContentAcquisitionEntity[], serviceList: Service[]) => {
        return caeList.map((cae) => {
            const territory = territoryList.find((territory) => territory.territoryId === cae.territoryID);

            return {
                serviceName: serviceList.find((service) => service.serviceId === cae.serviceID)?.serviceName || '',
                territoryCode: territory ? territory.territoryCode : '',
                defaultPaymentCurrency: territory ? territory.settlementCurrency : '',
                ...cae
            };
        }) as ICurrencyAndContentAcquisitionEntityRowData[];
    };

    const currencyContentAcquisitionEntityColSortModelContentColDefs = [
        {
            field: 'territoryCode',
            headerName: 'Territory',
            filter: 'agSetColumnFilter',
            minWidth: 120,
            cellRenderer: (params: ICellRendererParams) => getTerritoryFlagRenderer(params, true, true)
        },
        {
            field: 'serviceName',
            headerName: 'Service',
            filter: 'agSetColumnFilter',
            minWidth: 120
        },
        {
            field: 'defaultPaymentCurrency',
            headerName: 'Settlement Currency',
            filter: 'agSetColumnFilter',
            minWidth: 200
        },
        {
            field: 'contentAcquisitionEntityCode',
            headerName: 'Content Acquisition Entity',
            filter: 'agSetColumnFilter',
            minWidth: 250
        }
    ];

    const currencyContentAcquisitionEntityColSortModel: ColumnState[] = [
        { colId: 'territoryCode', sort: 'asc', sortIndex: 0 },
        { colId: 'serviceName', sort: 'desc', sortIndex: 1 }
    ];

    const currencyContentAcquisitionEntityColSortModelContent = (
        <Column className="currency-cae-div" spacing="300" width="100%" height="100%" minHeight={480}>
            <Box type="fill" spacingInset="400">
                <Row alignmentHorizontal="justify">
                    <FilterInput filterText={filterText} setFilterText={setFilterText} />
                </Row>
            </Box>
            <div className="currency-cae-grid">
                <BaseAgGrid
                    columnDefs={currencyContentAcquisitionEntityColSortModelContentColDefs}
                    sortModel={currencyContentAcquisitionEntityColSortModel}
                    rowData={currencyContentAcquisitionEntityRowData}
                    filterText={filterText}
                    loading={!isDataLoaded}
                />
            </div>
        </Column>
    );

    return (
        <div className="currency-cae-container">
            <Switch>
                <ProtectedRoute exact requiredRoleSet={VIEWER_ROLE_SET} path={path}>
                    {currencyContentAcquisitionEntityColSortModelContent}
                </ProtectedRoute>
            </Switch>
        </div>
    );
};
